'use client';

import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { API_URL } from '@eliadmin/core/src/api/fetcher';
import { fetchRelatedProducts } from '@eliadmin/core/src/api/products/related-products-api';
import {
  relatedProductsQueryKey,
  relatedProductsSession,
} from '@eliadmin/core/src/api/products/related-products-query';
import type { ProductDisplayData } from '@eliadmin/core/src/api/products/types/product-types';
import { ProductTile } from '@eliadmin/core/src/components/nt/product/product-tile';
import { Carousel } from '@eliadmin/core/src/components/nt/shared/carousel';
import { CarouselItem } from '@eliadmin/core/src/components/nt/shared/carousel-item';
import { env } from '@eliadmin/core/src/env';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';

/** Same columns as the homepage bestsellers: 260 px scrolling below md, 3 per view from md, 4 from lg. */
const TILE_SIZES =
  '(min-width: 1392px) 314px, (min-width: 1024px) calc((100vw - 136px) / 4), (min-width: 768px) calc((100vw - 96px) / 3), 260px';

/**
 * The related tiles in the header-arrow carousel. The page renders the public list; a logged-in
 * customer gets the same request with the auth token (customer prices, favourites) under the core
 * related-products query key (no token in the key).
 */
export function RelatedCarousel({
  locale,
  slug,
  products,
  header,
}: {
  locale: string;
  slug: string;
  products: ProductDisplayData[];
  header: ReactNode;
}) {
  const me = useCustomerStore(state => state.me);
  const currency = useGlobalStore(state => state.generalLayout?.currency);
  const { data } = useQuery({
    queryKey: relatedProductsQueryKey({
      api: API_URL,
      store: env.NEXT_PUBLIC_CURRENT_STORE,
      locale,
      slug,
      currency,
      session: relatedProductsSession(me),
      fields: {},
    }),
    queryFn: ({ signal }) =>
      fetchRelatedProducts({ locale, slug, authToken: me?.authToken, signal }),
    enabled: !!me?.authToken,
    staleTime: 60_000,
  });
  const items = me?.authToken && data?.data.length ? data.data : products;

  return (
    <Carousel
      header={header}
      className="flex flex-col gap-8"
      trackClassName="-mx-4 -my-1 gap-4 px-4 py-1 scroll-px-4 sm:-mx-6 sm:px-6 sm:scroll-px-6 md:-mx-1 md:gap-6 md:px-1 md:scroll-px-1"
    >
      {items.map(product => (
        <CarouselItem
          key={product.id}
          className="w-[260px] md:w-[calc((100%-48px)/3)] lg:w-[calc((100%-72px)/4)]"
        >
          <ProductTile product={product} imageSizes={TILE_SIZES} />
        </CarouselItem>
      ))}
    </Carousel>
  );
}
